import { useLocation } from "@tanstack/react-router";
import {
	createContext,
	type ReactNode,
	useContext,
	useRef,
	useState,
} from "react";
import { AssistDock, AssistProvider } from "@/components/assist/assist-shell";
import { CommandPalette } from "@/components/command-palette";
import { PublicOrgJoinBar } from "@/components/public-org-join-bar";
import {
	SettingsSectionBreadcrumb,
	WorkspaceBreadcrumbProvider,
} from "@/components/settings/workspace-breadcrumbs";
import { AppSidebar, CompactTopNav } from "@/components/sidebar/app-sidebar";
import { WorkspaceNavigationProvider } from "@/components/sidebar/use-workspace-navigation";
import { ViewAsVisitorBar } from "@/components/view-as-visitor-bar";
import { useContainerQuery } from "@/hooks/use-container-query";
import { isEmbeddedContext } from "@/lib/embedded-auth";
import { cn } from "@/lib/utils";

// Measured against the CONTAINER (ResizeObserver), not the window —
// `window.matchMedia` mis-reports inside the Chrome extension's iframe.
const NARROW_MAX = 640;

const IsNarrowContext = createContext<boolean>(false);

export function useIsNarrow(): boolean {
	return useContext(IsNarrowContext);
}

// auth / oauth flows render chrome-less (no workspace sidebar).
const STANDALONE = new Set(["auth", "oauth"]);
function isStandalone(pathname: string): boolean {
	const seg = pathname.split("/").filter(Boolean);
	return STANDALONE.has(seg[0] ?? "") || STANDALONE.has(seg[1] ?? "");
}

const SIDEBAR_COLLAPSED_KEY = "lobu.sidebar.collapsed";

export function ResponsiveAppShell({ children }: { children: ReactNode }) {
	const rootRef = useRef<HTMLDivElement | null>(null);
	// The Chrome extension's side panel always gets the top bar, however wide
	// the panel is dragged: a sidebar would eat its content.
	const [embedded] = useState(isEmbeddedContext);
	const containerNarrow = useContainerQuery(rootRef, NARROW_MAX);
	const isNarrow = embedded || containerNarrow;
	const location = useLocation();
	// Whether the sidebar is collapsed to its icons (wide viewports only),
	// persisted across reloads.
	const [collapsed, setCollapsed] = useState<boolean>(() => {
		try {
			return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
		} catch {
			return false;
		}
	});
	const toggleCollapsed = () => {
		setCollapsed((prev) => {
			const next = !prev;
			try {
				localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
			} catch {
				// ignore storage failures (private mode / extension sandbox)
			}
			return next;
		});
	};

	const standalone = isStandalone(location.pathname);
	// Content-area centering for the fixed assist popover: it centers on the
	// viewport, but the sidebar offsets content — shifting by half the
	// sidebar width re-centers it over the main column. The var lives on
	// this ancestor so the fixed descendant inherits it.
	const assistShift =
		standalone || isNarrow ? "0rem" : collapsed ? "1.75rem" : "8rem";

	return (
		<WorkspaceNavigationProvider>
			<WorkspaceBreadcrumbProvider>
				<IsNarrowContext.Provider value={isNarrow}>
					{/* Mounted at shell level so ⌘K works regardless of which branch renders. */}
					<CommandPalette />
					<div
						ref={rootRef}
						className="h-dvh overflow-hidden bg-background text-foreground"
						style={{ "--assist-shift": assistShift } as React.CSSProperties}
					>
						<AssistProvider>
							{standalone ? (
								// Auth and OAuth are already the sign-in flow. The join
								// bar here only offered "Sign in to join" again and
								// nested callbackUrl around /auth/login.
								<div className="flex h-full flex-col overflow-x-hidden overflow-y-auto">
									<ViewAsVisitorBar />
									{children}
								</div>
							) : isNarrow ? (
								<div className="flex h-dvh flex-col overflow-hidden">
									<header className="z-30 flex h-14 shrink-0 items-center gap-2 bg-background px-3">
										<CompactTopNav />
									</header>
									<ViewAsVisitorBar />
									<PublicOrgJoinBar />
									<main className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
										<SettingsSectionBreadcrumb />
										{children}
									</main>
									<AssistDock />
								</div>
							) : (
								<>
									<AppSidebar
										collapsed={collapsed}
										onToggleCollapsed={toggleCollapsed}
									/>
									<main
										className={cn(
											"flex h-dvh flex-col overflow-hidden transition-[padding] duration-200",
											collapsed ? "pl-14" : "pl-64",
										)}
									>
										<ViewAsVisitorBar />
										<PublicOrgJoinBar />
										<div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
											<SettingsSectionBreadcrumb />
											{children}
										</div>
										<AssistDock />
									</main>
								</>
							)}
						</AssistProvider>
					</div>
				</IsNarrowContext.Provider>
			</WorkspaceBreadcrumbProvider>
		</WorkspaceNavigationProvider>
	);
}
